// Content for the Permis ATNA « Le défi » section.
export type Bi = { fr: string; en: string }

export const CHALLENGE_IMAGES = {
  citizen: 'https://cdn.magicpatterns.com/patterns/generated-images/904d57f2-b2c9-486e-894a-aec8746367f8.jpg',
  townhall: 'https://cdn.magicpatterns.com/patterns/generated-images/e5e26dfb-9c78-43e2-9011-47393599f8d5.jpg',
  avatar: 'https://cdn.magicpatterns.com/patterns/generated-images/8604a75f-fffa-41a4-b6a9-101ab8fd41cb.jpg',
}

export const SOURCES = {
  burnaby: 'https://www.burnaby.ca/our-city/news/2026-08-26/city-burnaby-reviewing-permits-85-faster',
  csps: 'https://www.ecolecanada.gc.ca/tools/articles/simplifying-government-fra.aspx',
  deloitte:
    'https://www.deloitte.com/us/en/insights/industry/government-public-sector-services/government-trends/2024/radically-improving-the-government-customer-experience.html',
}

export const challengeCopy = {
  eyebrow: { fr: 'Le défi', en: 'The challenge' },
  line1: { fr: 'Obtenir un permis', en: 'Getting a permit' },
  line2: { fr: 'ne devrait pas être', en: 'shouldn’t be' },
  line3: { fr: 'aussi complexe.', en: 'this complicated.' },
  body: {
    fr: 'Aujourd’hui, les citoyens peinent à trouver l’information, les demandes sont souvent incomplètes et vos équipes passent un temps considérable à faire des suivis et des ressaisies, et à coordonner les validations.',
    en: 'Today, residents struggle to find information, applications often arrive incomplete, and your teams spend considerable time following up, re-entering data and coordinating reviews.',
  },
  question: { fr: 'Je veux construire une terrasse. Par où commencer ?',en: 'I want to build a deck. Where do I start?' },
  warning: { fr: 'Mon dossier est incomplet.', en: 'My file is incomplete.' },
  transition1: { fr: 'Le problème n’est pas seulement de mettre le permis en ligne.', en: 'The problem isn’t just putting the permit online.' },
  transition2: { fr: 'C’est tout le travail qui reste autour.', en: 'It’s all the work around it.' },
}

export const frictionSteps: { key: 'search' | 'form' | 'folder' | 'warn' | 'mail' | 'clock'; title: Bi; sub: Bi }[] = [
  { key: 'search', title: { fr: 'Trouver le bon permis', en: 'Find the right permit' }, sub: { fr: 'Plusieurs règles et documents.', en: 'Many rules and documents.' } },
  { key: 'form', title: { fr: 'Remplir le formulaire', en: 'Fill out the form' }, sub: { fr: 'Informations complexes.', en: 'Complex information.' } },
  { key: 'folder', title: { fr: 'Rassembler les documents', en: 'Gather the documents' }, sub: { fr: 'Plans, photos, etc.', en: 'Plans, photos, etc.' } },
  { key: 'warn', title: { fr: 'Soumettre la demande', en: 'Submit the application' }, sub: { fr: 'Dossier souvent incomplet.', en: 'Often incomplete.' } },
  { key: 'mail', title: { fr: 'Suivre et corriger', en: 'Follow up and correct' }, sub: { fr: 'Appels, courriels, ressaisies.', en: 'Calls, emails, re-entry.' } },
  { key: 'clock', title: { fr: 'Attendre une réponse', en: 'Wait for an answer' }, sub: { fr: 'Délais variables.', en: 'Unpredictable wait times.' } },
]

// `lead` is shown in darker weight, `rest` in muted text.
export const evidenceStats: { prefix?: Bi; value: Bi; lead: Bi; rest: Bi; source: Bi; href: string }[] = [
  {
    value: { fr: '1 770 h', en: '1,770 h' },
    lead: { fr: 'de travail du personnel économisées en un an',en: 'of staff time saved in one year' },
    rest: { fr: ' grâce au portail numérique de permis.', en: ' thanks to the digital permit portal.' },
    source: { fr: 'Ville de Burnaby · 2025', en: 'City of Burnaby · 2025' },
    href: SOURCES.burnaby,
  },
  {
    value: { fr: '21 points', en: '21 points' },
    lead: { fr: 'd’écart de satisfaction entre', en: 'satisfaction gap between' },
    rest: { fr: ' les services numériques gouvernementaux et privés.', en: ' government and private-sector digital services.' },
    source: { fr: 'Deloitte · 5 800 répondants · 13 pays', en: 'Deloitte · 5,800 respondents · 13 countries' },
    href: SOURCES.deloitte,
  },
  {
    prefix: { fr: 'Jusqu’à', en: 'Up to' },
    value: { fr: '85 %', en: '85%' },
    lead: { fr: 'de réduction du délai d’examen',en: 'reduction in review time' },
    rest: { fr: ' de certains permis résidentiels dans le cadre de la modernisation des processus de Burnaby.', en: ' for some residential permits as part of Burnaby’s process modernization.' },
    source: { fr: 'Ville de Burnaby · 2026', en: 'City of Burnaby · 2026' },
    href: SOURCES.burnaby,
  },
]

export const testimonial = {
  quote: { fr: '« Vous n’avez pas idée du temps qu’on va sauver. »',en: '“You have no idea how much time we’re going to save.”' },
  author: { fr: 'Équipe municipale', en: 'Municipal team' },
  place: 'Clarence-Rockland, Ontario',
}

export const insights: { key: 'people' | 'gear' | 'doc'; title: Bi; body: Bi; source: Bi; href?: string }[] = [
  {
    key: 'people',
    title: { fr: 'Formulaires mal conçus', en: 'Poorly designed forms' },
    body: {
      fr: 'Font perdre du temps, augmentent les erreurs et imposent une charge aux citoyens et aux gouvernements.',
      en: 'Waste time, increase errors and place a burden on both residents and governments.',
    },
    source: { fr: 'École de la fonction publique du Canada', en: 'Canada School of Public Service' },
    href: SOURCES.csps,
  },
  {
    key: 'gear',
    title: { fr: 'Capacité municipale sous pression', en: 'Municipal capacity under pressure' },
    body: {
      fr: 'Les processus manuels, les suivis, les dossiers incomplets et la ressaisie mobilisent un temps précieux des équipes.',
      en: 'Manual processes, follow-ups, incomplete files and re-entry take up valuable staff time.',
    },
    source: { fr: 'Contexte canadien des processus de permis', en: 'Canadian permitting context' },
  },
  {
    key: 'doc',
    title: { fr: 'Des citoyens moins satisfaits', en: 'Less satisfied residents' },
    body: {
      fr: 'Les services numériques gouvernementaux affichent un niveau de satisfaction inférieur de 21 points à celui du secteur privé.',
      en: 'Government digital services score 21 points lower in satisfaction than the private sector.',
    },
    source: { fr: 'Deloitte · 2024', en: 'Deloitte · 2024' },
    href: SOURCES.deloitte,
  },
]
